/**
 * useEnsureDefaultEffect — on mount, fire `POST /user-preferences/<scope>/<key>/seed-default`
 * exactly once per `sessionStorage` lifetime per `(scope, key, subKey)` triplet.
 *
 * BE will:
 *  - INSERT default row if missing                       → action 'seed-new'
 *  - MERGE missing sub-key from registry baseline        → action 'merge-subkey'
 *  - UPDATE differing sub-key from registry baseline     → action 'drift-update'
 *  - skip if identical                                   → action 'skip-identical' (no log)
 *
 * Used internally by `usePreference` and `usePreferenceColumns` so any page that
 * persists preferences auto-bootstraps its default row on first visit. No need
 * to call from pages directly.
 */
import { useEffect, useRef } from 'react'

import { useSeedPreferenceDefaultMutation } from '@/store/api/preferencesApi'

const sessionFlagKey = (scope: string, prefKey: string, subKey?: string): string =>
  `pref_seeded:${scope}:${prefKey}:${subKey ?? ''}`

function hasSeededThisSession(scope: string, prefKey: string, subKey?: string): boolean {
  try {
    return sessionStorage.getItem(sessionFlagKey(scope, prefKey, subKey)) === '1'
  } catch {
    return false
  }
}

function markSeededThisSession(scope: string, prefKey: string, subKey?: string): void {
  try {
    sessionStorage.setItem(sessionFlagKey(scope, prefKey, subKey), '1')
  } catch {
    // sessionStorage disabled (private mode / quota) — skip silently
  }
}

/**
 * Fire seed-default once per session per (scope, prefKey, subKey).
 *
 * @param scope     Preference scope (e.g. 'purchase').
 * @param prefKey   Preference key (e.g. 'columns', 'page_filter').
 * @param subKey    Optional sub-key for nested shape (e.g. 'forward-list').
 *                  Undefined for flat shapes (e.g. page_filter).
 */
export function useEnsureDefaultEffect(
  scope: string,
  prefKey: string,
  subKey?: string,
): void {
  const [seedDefault] = useSeedPreferenceDefaultMutation()
  const calledRef = useRef(false)

  useEffect(() => {
    if (calledRef.current) return
    if (hasSeededThisSession(scope, prefKey, subKey)) {
      calledRef.current = true
      return
    }
    calledRef.current = true
    void seedDefault({ scope, prefKey, subKey })
      .unwrap()
      .then(() => markSeededThisSession(scope, prefKey, subKey))
      .catch(() => {
        // Silent fail — never break page render if seed fails. Page will
        // read whatever default row currently exists (or empty), which is
        // the same as pre-feature behaviour.
        calledRef.current = false
      })
  }, [scope, prefKey, subKey, seedDefault])
}
